/**
 * Rodzina SYSTEM — strony testowe organu druku, per format.
 *
 * Katalogowy następca ręcznie sklejanej etykiety testowej EPL z PackFlow
 * (`routes/printing.js:591-613`). Wydruk testowy w panelu to PRAWDZIWE
 * zadanie przez prawdziwy pipeline (render → enqueue → claim → agent →
 * writeback) — zero specjalnych tras, zero `alert('wkrótce')`.
 *
 * Trzy wzory pokrywają pięć celów: etykieta termiczna (ZPL/EPL/TSPL),
 * paragon (ESC/POS) i strona A4 (PDF przez sterownik systemowy) — bo strona
 * testowa musi wyjść na TYM media, które testujemy, a media jest cechą wzoru.
 */

import { kompozycja, type PrintNode } from '../ir';
import { s, type PrintTemplateDef } from '../types';

/**
 * Zdanie z kompletem polskich diakrytyków — na termicznych dowodzi, że
 * transliteracja działa (zamiast krzaczków), na PDF że fonty mają glify.
 */
const PROBKA_PL = 'Zażółć gęślą jaźń — ĄĆĘŁŃÓŚŹŻ ąćęłńóśźż 0123456789';

const ZMIENNE_TESTOWE: PrintTemplateDef['variables'] = [
  {
    name: 'printed_at',
    description: 'Kiedy zlecono wydruk testowy.',
    example: '24.08.2026, 14:32',
    kind: 'date',
  },
  {
    name: 'printer_name',
    description: 'Nazwa testowanej drukarki z kartoteki.',
    example: 'Zebra ZD421 — pakowanie',
    optional: true,
  },
];

/** Wspólne elementy nagłówka strony testowej etykietowej. */
function naglowekTestowy(printedAt: string, printerName: string): PrintNode[] {
  return [
    { kind: 'text', xMm: 4, yMm: 4, text: 'INSOLITUM UNIVERSE', sizePt: 11, bold: true },
    { kind: 'text', xMm: 4, yMm: 10, text: 'STRONA TESTOWA ORGANU DRUKU', sizePt: 7 },
    { kind: 'text', xMm: 4, yMm: 15, text: `Zlecono: ${printedAt}`, sizePt: 7 },
    ...(printerName
      ? [{ kind: 'text', xMm: 4, yMm: 19, text: `Drukarka: ${printerName}`, sizePt: 7 } as PrintNode]
      : []),
  ];
}

export const systemTemplates: PrintTemplateDef[] = [
  // ---------------------------------------------------------------------------
  {
    slug: 'system.test_page',
    name: 'Strona testowa — etykieta termiczna',
    family: 'system',
    organ: 'Platforma',
    trigger: 'Przycisk „Wydruk testowy" w panelu /admin/printing dla drukarki etykietowej.',
    media: { kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 },
    targets: ['ZPL', 'EPL', 'TSPL', 'PDF'],
    version: 1,
    variables: ZMIENNE_TESTOWE,
    build: (v) =>
      kompozycja({ kind: 'label', widthMm: 80, heightMm: 50, dpi: 203 }, [
        // Ramka po obrysie — od razu widać przesunięcie kalibracji etykiety.
        { kind: 'box', xMm: 1, yMm: 1, widthMm: 78, heightMm: 48, thicknessMm: 0.4 },
        ...naglowekTestowy(s(v, 'printed_at'), s(v, 'printer_name')),
        { kind: 'line', xMm: 4, yMm: 23, x2Mm: 76, y2Mm: 23, thicknessMm: 0.4 },
        {
          kind: 'text',
          xMm: 4,
          yMm: 25,
          text: PROBKA_PL,
          sizePt: 7,
          maxWidthMm: 72,
        },
        {
          kind: 'barcode',
          xMm: 4,
          yMm: 34,
          data: 'UNIVERSE-TEST-128',
          symbology: 'code128',
          heightMm: 9,
          showText: true,
        },
        { kind: 'qr', xMm: 58, yMm: 30, data: 'universe:test-page', sizeMm: 16 },
      ]),
  },

  // ---------------------------------------------------------------------------
  {
    slug: 'system.test_page_receipt',
    name: 'Strona testowa — drukarka paragonowa',
    family: 'system',
    organ: 'Platforma',
    trigger: 'Przycisk „Wydruk testowy" w panelu /admin/printing dla drukarki ESC/POS.',
    media: { kind: 'document', page: 'receipt80mm' },
    targets: ['ESCPOS', 'PDF'],
    version: 1,
    variables: ZMIENNE_TESTOWE,
    build: (v) =>
      kompozycja({ kind: 'document', page: 'receipt80mm' }, [
        {
          kind: 'text',
          xMm: 0,
          yMm: 2,
          text: 'INSOLITUM UNIVERSE',
          sizePt: 14,
          bold: true,
          maxWidthMm: 72,
          align: 'center',
        },
        {
          kind: 'text',
          xMm: 0,
          yMm: 10,
          text: 'STRONA TESTOWA ORGANU DRUKU',
          sizePt: 8,
          maxWidthMm: 72,
          align: 'center',
        },
        { kind: 'line', xMm: 0, yMm: 16, x2Mm: 72, y2Mm: 16 },
        { kind: 'text', xMm: 0, yMm: 18, text: `Zlecono: ${s(v, 'printed_at')}`, sizePt: 8 },
        ...(s(v, 'printer_name')
          ? [
              {
                kind: 'text',
                xMm: 0,
                yMm: 23,
                text: `Drukarka: ${s(v, 'printer_name')}`,
                sizePt: 8,
              } as PrintNode,
            ]
          : []),
        { kind: 'text', xMm: 0, yMm: 28, text: PROBKA_PL, sizePt: 8, maxWidthMm: 72 },
        { kind: 'line', xMm: 0, yMm: 36, x2Mm: 72, y2Mm: 36 },
        {
          kind: 'barcode',
          xMm: 0,
          yMm: 38,
          data: 'UNIVERSE-TEST-128',
          symbology: 'code128',
          heightMm: 10,
          showText: true,
        },
        { kind: 'qr', xMm: 0, yMm: 52, data: 'universe:test-page', sizeMm: 18 },
      ]),
  },

  // ---------------------------------------------------------------------------
  {
    slug: 'system.test_page_a4',
    name: 'Strona testowa — dokument A4',
    family: 'system',
    organ: 'Platforma',
    trigger:
      'Przycisk „Wydruk testowy" w panelu /admin/printing dla drukarki systemowej (sterownik).',
    media: { kind: 'document', page: 'A4' },
    targets: ['PDF'],
    version: 1,
    variables: ZMIENNE_TESTOWE,
    build: (v) =>
      kompozycja({ kind: 'document', page: 'A4' }, [
        // Ramka blisko krawędzi obszaru zadruku — pokazuje realne marginesy sterownika.
        { kind: 'box', xMm: 2, yMm: 2, widthMm: 182, heightMm: 269, thicknessMm: 0.3 },
        { kind: 'text', xMm: 10, yMm: 12, text: 'INSOLITUM UNIVERSE', sizePt: 24, bold: true },
        {
          kind: 'text',
          xMm: 10,
          yMm: 24,
          text: 'STRONA TESTOWA ORGANU DRUKU — DOKUMENT A4',
          sizePt: 11,
        },
        { kind: 'line', xMm: 10, yMm: 32, x2Mm: 176, y2Mm: 32, thicknessMm: 0.3 },
        { kind: 'text', xMm: 10, yMm: 38, text: `Zlecono: ${s(v, 'printed_at')}`, sizePt: 10 },
        ...(s(v, 'printer_name')
          ? [
              {
                kind: 'text',
                xMm: 10,
                yMm: 45,
                text: `Drukarka: ${s(v, 'printer_name')}`,
                sizePt: 10,
              } as PrintNode,
            ]
          : []),
        { kind: 'text', xMm: 10, yMm: 56, text: PROBKA_PL, sizePt: 12 },
        {
          kind: 'text',
          xMm: 10,
          yMm: 66,
          text: 'Jeśli powyższe zdanie ma wszystkie polskie znaki, fonty obrazu renderującego są kompletne.',
          sizePt: 9,
          maxWidthMm: 166,
        },
        {
          kind: 'barcode',
          xMm: 10,
          yMm: 80,
          data: 'UNIVERSE-TEST-128',
          symbology: 'code128',
          heightMm: 14,
          showText: true,
        },
        { kind: 'qr', xMm: 150, yMm: 76, data: 'universe:test-page', sizeMm: 26 },
      ]),
  },
];
